import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, MapPin, Monitor, Smartphone, Tablet, Clock, 
  TrendingUp, TrendingDown, Eye, MousePointer, Globe,
  Chrome, Safari, Activity, ShoppingCart, Filter,
  ArrowRight, ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

const LiveVisitors = () => {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [peakHours, setPeakHours] = useState(null);
  const [shoppingBehavior, setShoppingBehavior] = useState(null);

  const fetchData = async () => {
    try {
      const [liveRes, peakRes, shoppingRes] = await Promise.all([
        axios.get(`${API}/api/analytics/live-visitors`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/analytics/peak-hours?period=7d`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/analytics/shopping-behavior?period=7d`, { headers: getAuthHeader() })
      ]);
      setData(liveRes.data);
      setPeakHours(peakRes.data);
      setShoppingBehavior(shoppingRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'mobile': return <Smartphone className="h-4 w-4" />;
      case 'tablet': return <Tablet className="h-4 w-4" />;
      default: return <Monitor className="h-4 w-4" />;
    }
  };

  const getSourceColor = (source) => {
    const colors = {
      'Google': 'bg-red-100 text-red-700',
      'Facebook': 'bg-blue-100 text-blue-700',
      'Direct': 'bg-slate-100 text-slate-700',
      'Instagram': 'bg-pink-100 text-pink-700',
      'Kijiji': 'bg-green-100 text-green-700',
      'AutoTrader': 'bg-orange-100 text-orange-700'
    };
    return colors[source] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-1/3"></div>
            <div className="h-32 bg-slate-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-slate-500">
          Impossible de charger les données
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main Stats */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2 text-slate-800">
              <Activity className="h-5 w-5 text-green-500" />
              Visiteurs en Direct
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchData} className="text-slate-500 hover:text-slate-800">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Active Now */}
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{data.total_active}</div>
              <div className="text-sm text-green-700">Actifs maintenant</div>
              <div className="flex items-center justify-center mt-1">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1"></span>
                <span className="text-xs text-green-600">En ligne</span>
              </div>
            </div>

            {/* Today Total */}
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">{data.today_total}</div>
              <div className="text-sm text-blue-700">Aujourd'hui</div>
              <div className="flex items-center justify-center mt-1 text-xs">
                {data.change_percent >= 0 ? (
                  <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                ) : (
                  <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
                )}
                <span className={data.change_percent >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {data.change_percent > 0 ? '+' : ''}{data.change_percent}%
                </span>
                <span className="text-slate-500 ml-1">vs hier</span>
              </div>
            </div>

            {/* Peak Hour */}
            {peakHours && (
              <div className="bg-amber-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-amber-600">{peakHours.peak_hour}h</div>
                <div className="text-sm text-amber-700">Heure de pointe</div>
                <div className="text-xs text-amber-600 mt-1">{peakHours.peak_day}</div>
              </div>
            )}

            {/* Conversion Rate */}
            {shoppingBehavior && (
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-purple-600">
                  {shoppingBehavior.conversion_funnel.conversion_rate}%
                </div>
                <div className="text-sm text-purple-700">Taux conversion</div>
                <div className="text-xs text-purple-600 mt-1">7 derniers jours</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Device & Source Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Devices */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Appareils</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(data.devices).map(([device, count]) => (
                <div key={device} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-slate-600">
                    {getDeviceIcon(device)}
                    <span className="text-sm capitalize">{device === 'mobile' ? 'Mobile' : device === 'tablet' ? 'Tablette' : 'Bureau'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full"
                        style={{ width: `${(count / Math.max(data.total_active, 1)) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium w-8 text-right text-slate-800">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Traffic Sources */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Sources de trafic</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(data.sources).slice(0, 5).map(([source, count]) => (
                <div key={source} className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded ${getSourceColor(source)}`}>{source}</span>
                  <span className="text-sm font-medium text-slate-800">{count}</span>
                </div>
              ))}
              {Object.keys(data.sources).length === 0 && (
                <div className="text-sm text-slate-500">Aucune donnée</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Locations */}
      {Object.keys(data.locations).length > 0 && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Emplacements des visiteurs actifs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(data.locations).map(([location, count]) => (
                <div key={location} className="flex items-center gap-1 bg-slate-100 px-3 py-1.5 rounded-full text-sm text-slate-700">
                  <MapPin className="h-3 w-3 text-slate-500" />
                  <span>{location}</span>
                  <span className="font-semibold text-blue-600">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Live Visitor List */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Visiteurs actifs ({data.visitors.length})
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="text-slate-500 hover:text-slate-800"
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
        {expanded && (
          <CardContent>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {data.visitors.map((visitor, idx) => (
                <div 
                  key={visitor.session_id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                  onClick={() => setSelectedVisitor(selectedVisitor === idx ? null : idx)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                      {getDeviceIcon(visitor.device)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-800">{visitor.location}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${getSourceColor(visitor.source)}`}>
                          {visitor.source}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span>{visitor.browser}</span>
                        <span>•</span>
                        <span>{visitor.pages_viewed} pages</span>
                        <span>•</span>
                        <span>{visitor.vehicles_viewed} véhicules</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-slate-800">{formatTime(visitor.time_on_site)}</div>
                    <div className="text-xs text-slate-500">{visitor.current_page}</div>
                  </div>
                </div>
              ))}
              {data.visitors.length === 0 && (
                <div className="text-center text-slate-500 py-8">
                  Aucun visiteur actif en ce moment
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Shopping Funnel */}
      {shoppingBehavior && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Entonnoir de conversion (7 jours)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 text-center">
                <div className="text-2xl font-bold text-slate-800">{shoppingBehavior.conversion_funnel.visitors}</div>
                <div className="text-xs text-slate-500">Visiteurs</div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-300" />
              <div className="flex-1 text-center">
                <div className="text-2xl font-bold text-blue-600">{shoppingBehavior.conversion_funnel.viewed_vehicles}</div>
                <div className="text-xs text-slate-500">Ont vu véhicules</div>
                <div className="text-xs text-blue-600">{shoppingBehavior.conversion_funnel.view_rate}%</div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-300" />
              <div className="flex-1 text-center">
                <div className="text-2xl font-bold text-amber-600">{shoppingBehavior.conversion_funnel.took_action}</div>
                <div className="text-xs text-slate-500">Actions</div>
                <div className="text-xs text-amber-600">{shoppingBehavior.conversion_funnel.action_rate}%</div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-300" />
              <div className="flex-1 text-center">
                <div className="text-2xl font-bold text-green-600">{shoppingBehavior.conversion_funnel.converted}</div>
                <div className="text-xs text-slate-500">Convertis</div>
                <div className="text-xs text-green-600">{shoppingBehavior.conversion_funnel.conversion_rate}%</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Searched Makes */}
      {shoppingBehavior && shoppingBehavior.filter_usage.top_makes.length > 0 && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Marques les plus recherchées
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {shoppingBehavior.filter_usage.top_makes.map(([make, count]) => (
                <div key={make} className="flex items-center gap-1 bg-blue-50 px-3 py-1.5 rounded-full text-sm">
                  <span className="text-blue-700">{make}</span>
                  <span className="font-semibold text-blue-900">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vehicles Being Viewed */}
      {data.vehicles_being_viewed.length > 0 && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Véhicules consultés maintenant
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {data.vehicles_being_viewed.map((vehicle) => (
                <div key={vehicle.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  {vehicle.images?.[0] && (
                    <img 
                      src={vehicle.images[0]} 
                      alt={`${vehicle.year} ${vehicle.make}`}
                      className="w-12 h-9 object-cover rounded"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate text-slate-800">
                      {vehicle.year} {vehicle.make}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{vehicle.model}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LiveVisitors;
